import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  Terminal,
  CheckCircle2,
  Calendar,
  Award,
  Users,
  BookOpen,
  ArrowRight,
  MapPin,
  Clock,
  Sparkles,
  ExternalLink,
} from "lucide-react";

export const LandingPage = () => {
  const highlights = [
    {
      title: "Tuition-Free Masterclass",
      desc: "15 days of live hands-on practical training with no tuition fee.",
      icon: <Award className="w-5 h-5 text-emerald-400" />,
    },
    {
      title: "Hands-on Cyber Lab",
      desc: "Practical labs on Ethical Hacking, Network Security, Web App Penetration, and SOC.",
      icon: <Terminal className="w-5 h-5 text-cyan-400" />,
    },
    {
      title: "Industry Mentorship",
      desc: "Taught by active security analysts and cybersecurity researchers from Kian Technologies.",
      icon: <Users className="w-5 h-5 text-emerald-400" />,
    },
    {
      title: "Verified Certificate",
      desc: "Earn a verified Masterclass Completion Certificate upon fulfilling the 85%+ attendance criteria.",
      icon: <Shield className="w-5 h-5 text-cyan-400" />,
    },
  ];

  const curriculum = [
    { day: "Day 01", topic: "Introduction to Cyber Security Landscape & Threat Vectors" },
    { day: "Day 02", topic: "Networking Fundamentals for Hackers (TCP/IP, OSI, Wireshark)" },
    { day: "Day 03", topic: "Linux Command Line Mastery & Security Administration" },
    { day: "Day 04", topic: "Information Gathering & Passive/Active Reconnaissance (OSINT)" },
    { day: "Day 05", topic: "Vulnerability Scanning & Assessment (Nmap, Nessus, OpenVAS)" },
    { day: "Day 06", topic: "Web Application Security & OWASP Top 10 (SQLi, XSS, CSRF)" },
    { day: "Day 07", topic: "Hands-on Burp Suite: Intercepting, Fuzzing & Exploitation" },
    { day: "Day 08", topic: "Cryptography, Hashing & Public Key Infrastructure (PKI)" },
    { day: "Day 09", topic: "Network Attacks: MITM, ARP Spoofing & Wireless Security" },
    { day: "Day 10", topic: "System Hacking & Windows/Linux Privilege Escalation" },
    { day: "Day 11", topic: "Malware Analysis Basics & Endpoint Protection Mechanisms" },
    { day: "Day 12", topic: "Security Operations Center (SOC) & SIEM Architecture" },
    { day: "Day 13", topic: "Digital Forensics & Incident Response (DFIR) Fundamentals" },
    { day: "Day 14", topic: "Capture The Flag (CTF) Challenge & Live Attack Simulation" },
    { day: "Day 15", topic: "Career Pathways, Certifications (CEH, OSCP, CISSP) & Q&A" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-20 md:py-24 border-b border-border/40">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span>Registration Open for 2026 Batch • Bhilai Campus</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
                15 Days Tuition-Free <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">
                  Cyber Security Masterclass
                </span>
              </h1>
              
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Empowering college students, aspiring ethical hackers, and tech enthusiasts in 
                <strong className="text-foreground"> Bhilai & Chhattisgarh</strong> with real-world defensive and offensive cyber security skills.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link
                  to="/register"
                  data-testid="hero-apply-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono font-medium shadow-lg shadow-emerald-950/40 transition-all hover:scale-[1.02]"
                >
                  <span>Apply For Free Seat</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  data-testid="hero-login-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-border bg-card hover:bg-muted text-foreground font-mono text-sm font-medium transition-all"
                >
                  <Lock className="w-4 h-4 text-emerald-400" />
                  <span>Student Portal Login</span>
                </Link>
              </div>

              {/* Key Quick Badges */}
              <div className="pt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-border/60 text-xs font-mono text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-emerald-400" />
                  <span>15 Daily Modules</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                  <span>Bhilai, Chhattisgarh</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-emerald-400" />
                  <span>Free Verification</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="p-6 rounded-2xl bg-card border border-border shadow-xl relative backdrop-blur-sm">
                <div className="flex items-center justify-between border-b border-border pb-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    <span className="font-bold text-sm tracking-wide">Portal Status & Admission</span>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    Live
                  </span>
                </div>

                <div className="space-y-4 text-xs font-mono">
                  <div className="p-3 rounded-lg bg-muted/60 border border-border space-y-1">
                    <div className="text-muted-foreground">Organization</div>
                    <div className="text-foreground font-semibold">Kian Technologies, Bhilai</div>
                  </div>

                  <div className="p-3 rounded-lg bg-muted/60 border border-border space-y-1">
                    <div className="text-muted-foreground">Masterclass Eligibility</div>
                    <div className="text-foreground font-semibold">College Students (Any Year / Branch) & Beginners</div>
                  </div>

                  <div className="p-3 rounded-lg bg-muted/60 border border-border space-y-1">
                    <div className="text-muted-foreground">Verification Process</div>
                    <div className="text-foreground">
                      Applications verified manually by the Kian Tech cyber team. Approved students receive verified ID credentials.
                    </div>
                  </div>

                  <div className="pt-2">
                    <Link
                      to="/register"
                      className="w-full py-2.5 px-4 rounded-lg bg-emerald-600/90 hover:bg-emerald-500 text-white font-mono text-xs font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      Start Free Registration Form →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Highlights */}
      <section className="py-16 bg-muted/20 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Why Join the Kian Cyber Security Masterclass?
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              Structured to give aspiring security professionals an actionable foundation in defensive & offensive techniques.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, idx) => (
              <div
                key={idx}
                className="p-6 rounded-xl bg-card border border-border hover:border-emerald-500/40 transition-all shadow-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mb-4 border border-border">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-base mb-2">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 15 Days Masterclass Curriculum */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <span className="text-xs font-mono text-emerald-400 uppercase tracking-wider">
                Full 15-Day Roadmap
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">
                Masterclass Syllabus & Practical Modules
              </h2>
            </div>
            <Link
              to="/register"
              className="text-xs font-mono text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 font-semibold"
            >
              Reserve your seat now <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {curriculum.map((item, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg bg-card border border-border flex items-start space-x-3 hover:bg-muted/40 transition-colors"
              >
                <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 font-mono text-[11px] font-bold border border-emerald-500/20 flex-shrink-0">
                  {item.day}
                </span>
                <span className="text-xs font-medium text-foreground leading-snug">
                  {item.topic}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-t from-emerald-950/20 to-transparent border-t border-border/40 text-center">
        <div className="max-w-3xl mx-auto px-4 space-y-6">
          <Shield className="w-12 h-12 text-emerald-400 mx-auto" />
          <h2 className="text-3xl font-bold tracking-tight">
            Ready to Begin Your Cyber Security Journey?
          </h2>
          <p className="text-sm text-muted-foreground">
            Seats are allocated on a verified first-come basis for students in Bhilai and surrounding regions.
          </p>
          <p className="text-[11px] text-muted-foreground/80 font-mono">Tuition-free program • ₹599 one-time registration/administrative fee applies to confirmed registration.</p>
          <div className="flex justify-center gap-4 pt-2">
            <Link
              to="/register"
              className="px-6 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono font-medium shadow-md transition-all"
            >
              Submit Free Application
            </Link>
            <Link
              to="/login"
              className="px-6 py-3 rounded-lg border border-border bg-card hover:bg-muted text-foreground font-mono text-sm font-medium transition-all"
            >
              Existing Student Login
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
